"use client";

import { useMe, useParams } from "@/hooks";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/hooks/usePermission/usePermission";
import { AppDispatch, RootState } from "@/store";
import { fetchClients } from "@/store/modules/clients/thunks";
import { fetchEmployees } from "@/store/modules/employees/thunks";
import { fetchPaymentMethods } from "@/store/modules/paymentMethod/thunks";
import { fetchProducts } from "@/store/modules/products/thunks";
import {
  createScheduleProduct,
  deleteScheduleProduct,
  updateScheduleProduct,
} from "@/store/modules/scheduleRelationProduct/thunks";
import {
  createScheduleService,
  deleteScheduleService,
  updateScheduleService,
} from "@/store/modules/scheduleRelationService/thunks";
import {
  blockedSchedule,
  createSchedule,
  deleteSchedule,
  fetchSchedule,
  finalizeSchedule,
  markScheduleAs,
  reopenSchedule,
  updateSchedule,
} from "@/store/modules/schedules/thunks";
import {
  fetchServiceByClient,
  fetchServices,
} from "@/store/modules/services/thunks";
import { Schedule, ScheduleFinalizePayload, User } from "@/types";
import { format } from "date-fns";
import { scheduleSchema } from "@/validations";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSession } from "next-auth/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { fetchSetting } from "@/store/modules/settings/thunks";
import { fetchCashRegister } from "@/store/modules/cashier/thunk";

const useSchedule = (onSuccess?: () => void) => {
  const { data: session } = useSession();

  const { hasPermission, loading: permissionsLoading } = usePermission();
  const { establishment, baseURL } = useAuth();

  const { params, setParams, handlePage } = useParams();
  const dispatch: AppDispatch = useDispatch();

  const [scheduleId, setScheduleId] = useState<number | null>(null);
  const [serviceID, setServiceID] = useState<number | null>(null);
  const [productId, setProductIdRelation] = useState<number | null>(null);
  const [loadingConfirmation, setLoadingConfirmation] =
    useState<boolean>(false);
  const [payments, setPayments] = useState<{ id: string; value: number }[]>([]);
  const [totalValueCalc, setTotalValueCalc] = useState<number>(0);
  const [isFinalize, setIsFinalize] = useState<boolean>(false);

  const [subscriptionItem, setSubscriptionItem] = useState<any>(false);
  const [subscriptionUsageItem, setSubscriptionUsageItem] =
    useState<any>(false);

  const hasInitializedRef = useRef(false);
  const [loadingModalData, setLoadingModalData] = useState(false);

  const paramsStringified = useMemo(() => JSON.stringify(params), [params]);
  const paramsStableRef = useRef(params);

  useEffect(() => {
    paramsStableRef.current = params;
  }, [params]);

  const refreshSchedule = useCallback(
    async (date?: string | Date) => {
      console.log("chegou aqui");
      const getDateString = (d?: string | Date) => {
        if (!d) return null;
        if (d instanceof Date) return format(d, "yyyy-MM-dd");
        return String(d);
      };

      // Determine date precedence: explicit `date` arg -> params.start_date/date -> today
      const explicit = getDateString(date);
      const fromParams =
        (paramsStableRef.current as any)?.start_date ||
        (paramsStableRef.current as any)?.date ||
        null;
      const dateStr =
        explicit || fromParams || format(new Date(), "yyyy-MM-dd");

      const finalParams: any = {
        ...(paramsStableRef.current as any),
        start_date: dateStr,
        end_date: dateStr,
      };

      // Persist the requested date into params so subsequent refreshes
      // (that don't pass an explicit date) use the same calendar date.
      try {
        setParams({
          ...(paramsStableRef.current as any),
          start_date: dateStr,
          end_date: dateStr,
        });
      } catch (e) {
        // ignore setParams errors
      }
      // Keep the stable ref in sync immediately to avoid timing issues
      paramsStableRef.current = {
        ...(paramsStableRef.current as any),
        start_date: dateStr,
        end_date: dateStr,
      };

      await dispatch(fetchSchedule(finalParams as any));
    },
    [dispatch],
  );

  const safeFetchCashRegister = (payload: any) => {
    if (!baseURL || !establishment?.id) return Promise.resolve(null);

    const paramsAny = paramsStableRef.current as any;
    const dateFromParams = payload?.date || paramsAny?.date || null;

    const date = dateFromParams || format(new Date(), "yyyy-MM-dd");

    const finalPayload = { ...paramsAny, ...payload, date };

    return dispatch(fetchCashRegister(finalPayload));
  };

  useEffect(() => {
    if (permissionsLoading || !session) return;
    if (!baseURL || !establishment) return;
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const loadData = async () => {
      setLoadingConfirmation(true);

      try {
        const promises: Promise<unknown>[] = [];

        const today = format(new Date(), "yyyy-MM-dd");
        const initialParams: any = {
          ...(paramsStableRef.current as any),
          start_date: today,
          end_date: today,
        };
        promises.push(dispatch(fetchSchedule(initialParams as any)));
        promises.push(dispatch(fetchEmployees({ without_pagination: 1 })));
        promises.push(dispatch(fetchSetting({ without_pagination: 1 })));
        promises.push(
          safeFetchCashRegister({
            ...paramsStableRef.current,
            without_pagination: 1,
          }),
        );

        if (promises.length) {
          await Promise.allSettled(promises);
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      } finally {
        setLoadingConfirmation(false);
      }
    };

    void loadData();
  }, [
    dispatch,
    hasPermission,
    permissionsLoading,
    session,
    baseURL,
    establishment,
  ]);

  const loadModalData = useCallback(async () => {
    setLoadingModalData(true);

    try {
      const promises: Promise<unknown>[] = [];

      promises.push(dispatch(fetchClients({ without_pagination: 1 })));
      // Carrega 200 serviços para a agenda (não todos, mas suficiente para o select)
      promises.push(dispatch(fetchServices({ per_page: 200 })));
      promises.push(
        dispatch(fetchProducts({ limit: 200, without_pagination: 1 })),
      );
      promises.push(dispatch(fetchSetting({ without_pagination: 1 })));
      promises.push(
        safeFetchCashRegister({
          ...paramsStableRef.current,
          without_pagination: 1,
        }),
      );
      if (promises.length) {
        await Promise.allSettled(promises);
      }
    } catch (error) {
      console.error("Erro ao carregar dados do modal:", error);
    } finally {
      setLoadingModalData(false);
    }
  }, [dispatch, hasPermission, paramsStringified]);

  const { schedules, loading, error } = useSelector(
    (state: RootState) => state.schedules,
  );

  // Helper to coerce paginated envelopes or raw arrays into arrays
  const toArray = (v: any) => {
    if (!v) return [];
    if (Array.isArray(v)) return v;
    if (Array.isArray(v.data)) return v.data;
    return [];
  };
  const { clients } = useSelector((state: RootState) => state.clients);
  const { products } = useSelector((state: RootState) => state.products);
  const { employees } = useSelector((state: RootState) => state.employees);
  const { paymentMethods } = useSelector(
    (state: RootState) => state.paymentMethods,
  );

  // Read subscription config (may include weekly_schedule_limit)
  const subscriptionConfig = useSelector(
    (state: RootState) =>
      (state.subscriptionConfig?.data?.data ??
        state.subscriptionConfig?.data) ||
      null,
  );
  const { services } = useSelector((state: RootState) => state.services);
  const { servicesByClient } = useSelector(
    (state: RootState) => state.services,
  );
  const { categories } = useSelector((state: RootState) => state.categories);
  const { setting } = useSelector((state: RootState) => state.settings);
  const { cashier } = useSelector((state: RootState) => state.cashier);

  const loadPaymentMethodsOnDemand = useCallback(async () => {
    if (paymentMethods?.data && paymentMethods.data.length > 0) {
      return;
    }

    try {
      await dispatch(fetchPaymentMethods({ without_pagination: 1 }));
    } catch (error) {
      console.error("Erro ao carregar métodos de pagamento:", error);
    }
  }, [dispatch, paymentMethods, hasPermission]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
    control,
    clearErrors,
  } = useForm({
    resolver: yupResolver(scheduleSchema),
    defaultValues: {
      client_id: null,
      is_fit_in: false,
      products: [],
      service_id: [],
      // Tracks selection origin for each service id index (keep in sync with service_id[])
      service_origin: [],
      start_date_time: "",
      closing_date_time: null,
      duration: null,
      status: "",
      type: "scheduled",
      user_id: null,
    },
  });

  const setFormData = useCallback(
    (data: Partial<typeof scheduleSchema.__outputType>) => {
      reset(data);
    },
    [reset],
  );

  const onSubmit = useCallback(
    async (data: typeof scheduleSchema.__outputType) => {
      try {
        const formatDateTime = (dateString: string | Date): string => {
          const date =
            typeof dateString === "string" ? new Date(dateString) : dateString;
          if (isNaN(date.getTime())) return dateString as string;
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const hours = String(date.getHours()).padStart(2, "0");
          const minutes = String(date.getMinutes()).padStart(2, "0");
          return `${year}-${month}-${day}T${hours}:${minutes}`;
        };

        const processedData: any = { ...data };
        if (processedData.closing_date_time) {
          processedData.closing_date_time = formatDateTime(
            processedData.closing_date_time as any,
          ) as any;
        }

        // If user provided service_id array, convert to new 'services' payload expected by backend
        if (Array.isArray(processedData.service_id)) {
          const allServices: any[] = [
            ...toArray(servicesByClient),
            ...toArray(services),
          ];

          const findClientService = (id: number) =>
            toArray(servicesByClient).find(
              (s: any) => Number(s?.id) === Number(id),
            );
          const findBaseService = (id: number) =>
            toArray(services).find((s: any) => Number(s?.id) === Number(id));

          const isSubscriptionServiceRecord = (id: number) => {
            const clientSvc = findClientService(id);
            const baseSvc = findBaseService(id);

            // Treat a service as subscription-type if ANY record (client or base) indicates it
            const check = (s: any) =>
              !!s &&
              (s.is_subscription === true ||
                s.valid_in_subscription === true ||
                (s.subscription_status &&
                  String(s.subscription_status).toLowerCase() === "active"));

            return check(clientSvc) || check(baseSvc);
          };

          // Map service_id -> services[{id, use_subscription}]
          // We now allow the UI to indicate origin (subscription/package/single) via
          // `service_origin` array at the same indices. If origin === 'subscription'
          // we force use_subscription=true. If origin === 'single' we force false.
          // Otherwise fallback to service record inspection.
          processedData.services = processedData.service_id.map(
            (sid: any, idx: number) => {
              const idNum = Number(sid);
              const origin = Array.isArray(processedData.service_origin)
                ? processedData.service_origin[idx]
                : undefined;

              let useSub: boolean;
              if (origin === "subscription") {
                useSub = true;
              } else if (origin === "single" || origin === "package") {
                useSub = false;
              } else {
                useSub = isSubscriptionServiceRecord(idNum);
              }

              return { id: idNum, use_subscription: Boolean(useSub) };
            },
          );

          // Clean up temporary origin markers
          delete processedData.service_origin;

          delete processedData.service_id;

          // Client-side weekly limit validation: consider a service as subscription if
          // 1) it's marked use_subscription in payload OR
          // 2) the service record itself is subscription-type (some backend validations use the service flags)
          try {
            const weeklyLimitRaw = subscriptionConfig?.weekly_schedule_limit;
            const weeklyLimit = Number(weeklyLimitRaw ?? NaN);
            if (Number.isFinite(weeklyLimit) && weeklyLimit > 0) {
              const startDate = new Date(processedData.start_date_time as any);
              const day = startDate.getDay();
              const diffToMonday = (day + 6) % 7; // days to subtract to get Monday
              const monday = new Date(startDate);
              monday.setDate(startDate.getDate() - diffToMonday);
              monday.setHours(0, 0, 0, 0);
              const sunday = new Date(monday);
              sunday.setDate(monday.getDate() + 6);
              sunday.setHours(23, 59, 59, 999);

              const servicesToCheck = processedData.services.filter(
                (s: any) => {
                  const idNum = Number(s.id);
                  return (
                    Boolean(s.use_subscription) ||
                    isSubscriptionServiceRecord(idNum)
                  );
                },
              );

              if (servicesToCheck.length > 0) {
                const schedulesForClient = (schedules?.data || []).filter(
                  (sch: any) =>
                    Number(sch.client_id) === Number(processedData.client_id),
                );

                for (const svc of servicesToCheck) {
                  const svcId = Number(svc.id);
                  let count = 0;
                  for (const sch of schedulesForClient) {
                    const schDateRaw = sch.start_date_time ?? null;
                    if (!schDateRaw) continue;
                    const schDate = new Date(schDateRaw);
                    if (isNaN(schDate.getTime())) continue;
                    if (schDate < monday || schDate > sunday) continue;

                    const ticketItems = sch?.ticket?.ticket_items || [];
                    const hasService = ticketItems.some(
                      (ti: any) =>
                        ti?.ticketable_type === "Service" &&
                        Number(ti?.ticketable_id) === svcId,
                    );
                    if (hasService) count++;
                  }

                  const projected = count + 1;
                  if (projected > weeklyLimit) {
                    const fmt = (d: Date) =>
                      `${String(d.getDate()).padStart(2, "0")}/${String(
                        d.getMonth() + 1,
                      ).padStart(2, "0")}/${d.getFullYear()}`;
                    const msg = `Já existem ${count} agendamentos de serviço de assinatura nesta semana (${fmt(
                      monday,
                    )} a ${fmt(sunday)}). Limite por semana: ${weeklyLimit}.`;
                    toast.error(msg);
                    throw new Error(msg);
                  }
                }
              }
            }
          } catch (e) {
            throw e;
          }
        }

        const payload = scheduleId
          ? {
              id: scheduleId,
              ...processedData,
              type: processedData?.type ?? "scheduled",
            }
          : {
              ...processedData,
              type: processedData?.type ?? "scheduled",
            };

        const action = scheduleId
          ? updateSchedule(payload as any)
          : createSchedule(payload as any);

        const result = await dispatch(action);

        if (
          createSchedule.fulfilled.match(result) ||
          updateSchedule.fulfilled.match(result)
        ) {
          await refreshSchedule();
        }
      } catch (error) {
        console.error("Submission error:", error);
        throw error;
      } finally {
        reset();
        setScheduleId(null);
        onSuccess?.();
      }
    },
    [scheduleId, dispatch, refreshSchedule, reset, onSuccess],
  );

  const getServiceByClient = useCallback(
    async (clientId: number | null, startDateTime?: string) => {
      try {
        if (!clientId || clientId === -1) {
          return;
        }

        await dispatch(
          fetchServiceByClient({
            client: clientId,
            startDateTime,
          }),
        );
      } catch (error) {
        console.error("Erro ao buscar serviços do cliente:", error);
      }
    },
    [dispatch],
  );

  const handleDispatchSchedule = useCallback(
    async (date?: string | Date) => {
      await refreshSchedule(date);
    },
    [refreshSchedule],
  );

  const handleDelete = useCallback(async () => {
    try {
      if (scheduleId) {
        await dispatch(deleteSchedule(scheduleId));
        setScheduleId(null);
        await refreshSchedule();
        return true;
      }
    } catch (error) {
      console.error("Erro ao deletar agendamento:", error);
    }
  }, [scheduleId, dispatch, refreshSchedule]);

  const handleDeleteService = useCallback(
    async (serviceId?: number) => {
      try {
        const targetServiceId = serviceId || serviceID;
        if (scheduleId && targetServiceId) {
          await dispatch(
            deleteScheduleService({
              scheduleId,
              serviceId: targetServiceId,
            }),
          );
          setServiceID(null);
          await refreshSchedule();
          return true;
        }
      } catch (error) {
        console.error("Erro ao deletar serviço:", error);
      }
    },
    [scheduleId, serviceID, dispatch, refreshSchedule],
  );

  const handleDeleteProduct = useCallback(
    async (targetProductId?: number) => {
      try {
        const productIdToDelete = targetProductId || productId;
        if (scheduleId && productIdToDelete) {
          await dispatch(
            deleteScheduleProduct({
              scheduleId,
              productId: productIdToDelete,
            }),
          );
          setProductIdRelation(null);
          await refreshSchedule();
          return true;
        }
      } catch (error) {
        console.error("Erro ao deletar produto:", error);
      }
    },
    [scheduleId, productId, dispatch, refreshSchedule],
  );

  const storeNewService = useCallback(
    async (options?: {
      use_package?: boolean;
      service_id_override?: number | string | null;
      originTab?: "single" | "subscription" | "package";
    }) => {
      try {
        const sid = options?.service_id_override ?? serviceID;
        if (!scheduleId || !sid) return;

        // Build payload according to requested origin tab or options
        let serviceData: any;

        if (options?.originTab === "subscription") {
          // Backend expects an array with use_subscription flag for subscription adds
          serviceData = [
            {
              id: typeof sid === "number" ? sid : Number(sid),
              use_subscription: true,
            },
          ];
        } else if (options?.originTab === "single") {
          // Avulso: explicit use_subscription false
          serviceData = [
            {
              id: typeof sid === "number" ? sid : Number(sid),
              use_subscription: false,
            },
          ];
        } else if (options?.originTab === "package") {
          // Package: indicate use_package
          serviceData = [
            {
              id: typeof sid === "number" ? sid : Number(sid),
              use_package: true,
            },
          ];
        } else {
          // Fallback to legacy single-object payload
          serviceData = {
            service_id: typeof sid === "number" ? sid : Number(sid),
          };
        }

        await dispatch(createScheduleService({ scheduleId, serviceData }));

        await refreshSchedule();
        setServiceID(null);
        return true;
      } catch (error) {
        console.error("Erro ao criar serviço:", error);
      }
    },
    [scheduleId, serviceID, dispatch, refreshSchedule],
  );

  const storeProduct = useCallback(async () => {
    try {
      if (!scheduleId || !productId) return;

      const productData = { product_id: productId };

      await dispatch(createScheduleProduct({ scheduleId, productData }));

      await refreshSchedule();
      setProductIdRelation(null);
      return true;
    } catch (error) {
      console.error("Erro ao criar produto:", error);
    }
  }, [scheduleId, productId, dispatch, refreshSchedule]);

  const updateScheduleProductQuantity = useCallback(
    async (targetProductId?: number, quantity?: number) => {
      try {
        const productIdToUpdate = targetProductId ?? productId ?? undefined;
        if (scheduleId && productIdToUpdate && quantity != null) {
          await dispatch(
            updateScheduleProduct({
              scheduleId,
              productId: productIdToUpdate,
              data: { quantity },
            }),
          );
          await refreshSchedule();
          return true;
        }
      } catch (error) {
        console.error("Erro ao atualizar quantidade do produto:", error);
      }
    },
    [scheduleId, productId, dispatch, refreshSchedule],
  );

  const updateScheduleServiceQuantity = useCallback(
    async (targetServiceId?: number, quantity?: number) => {
      try {
        const serviceIdToUpdate = targetServiceId ?? serviceID ?? undefined;
        if (scheduleId && serviceIdToUpdate && quantity != null) {
          await dispatch(
            updateScheduleService({
              scheduleId,
              serviceId: serviceIdToUpdate,
              data: { quantity },
            }),
          );
          await refreshSchedule();
          return true;
        }
      } catch (error) {
        console.error("Erro ao atualizar quantidade do serviço:", error);
      }
    },
    [scheduleId, serviceID, dispatch, refreshSchedule],
  );

  const markAs = useCallback(
    async (status: string) => {
      try {
        if (scheduleId) {
          await dispatch(markScheduleAs({ scheduleId, status }));
          await refreshSchedule();
          onSuccess?.();
          reset();
          setScheduleId(null);
          return true;
        }
      } catch (error) {
        console.error("Erro ao marcar agendamento:", error);
      }
    },
    [scheduleId, dispatch, refreshSchedule, onSuccess, reset],
  );

  const handleBlockedTime = useCallback(
    async (
      data: any,
      setIsBloqueMode: React.Dispatch<React.SetStateAction<boolean>>,
    ) => {
      const scheduleItem = schedules?.data?.find(
        (item: Schedule) =>
          item.establishment_id === session?.user?.user?.establishment_id,
      );

      try {
        if (scheduleItem?.id) {
          console.debug("[handleBlockedTime] dispatching blockedSchedule", {
            scheduleId: scheduleItem.id,
            data,
          });
          const res: any = await dispatch(
            blockedSchedule({ data, scheduleId: scheduleItem.id }),
          );

          if (res?.error) {
            console.error("blockedSchedule rejected:", res);
            return false;
          }

          await refreshSchedule();
          setIsBloqueMode(false);
          return true;
        }
      } catch (error) {
        console.error("Erro ao bloquear horário:", error);
        return false;
      }
    },
    [schedules, session, dispatch, refreshSchedule],
  );

  const handleFinalize = useCallback(
    async (
      tip_amount: number,
      discount_amount: number,
      total_value: number,
    ) => {
      const totalPaidAmount = payments.reduce((sum, p) => sum + p.value, 0);

      if (total_value > 0) {
        if (totalPaidAmount <= 0 && totalPaidAmount < total_value) {
          toast.error(
            "Algo está errado. Verifique o valor a pagar ou adicione um meio de pagamento.",
          );
          return;
        }

        if (totalValueCalc < 0) {
          toast.error("O total a pagar é maior que o valor devido.");
          return;
        }

        if (totalValueCalc > 0) {
          toast.error("O total a pagar é menor que o valor devido.");
          return;
        }
      }

      setIsFinalize(true);

      const payload: ScheduleFinalizePayload = {
        discount_amount: Number(discount_amount).toFixed(2) ?? 0,
        tip_amount: Number(tip_amount).toFixed(2) ?? 0,
      };

      if (total_value > 0 && payments.length > 0) {
        const formattedPayments = payments.map((p) => ({
          paid_amount: p.value,
          payment_method_id: Number(p.id),
        }));
        payload.payment_details = formattedPayments;
      }

      try {
        if (scheduleId) {
          await dispatch(finalizeSchedule({ data: payload, scheduleId }));
          setPayments([]);
          await refreshSchedule();
          onSuccess?.();
          return true;
        }
      } catch (error) {
        console.error("Erro ao finalizar agendamento:", error);
      } finally {
        setIsFinalize(false);
      }
    },
    [
      payments,
      totalValueCalc,
      scheduleId,
      dispatch,
      refreshSchedule,
      onSuccess,
    ],
  );

  const reopen = useCallback(async () => {
    try {
      if (scheduleId) {
        await dispatch(reopenSchedule({ scheduleId }));
        await refreshSchedule();
        onSuccess?.();
        reset();
        setScheduleId(null);
        return true;
      }
    } catch (error) {
      console.error("Erro ao reabrir agendamento:", error);
    }
  }, [scheduleId, dispatch, refreshSchedule, onSuccess, reset]);

  const getMissingRequirements = useCallback(() => {
    const missing: {
      name: string;
      message: string;
      redirect: string;
    }[] = [];

    const hasClients = clients?.data?.length > 0;
    const hasServices = services?.data?.length > 0;
    const hasEmployees = employees?.data?.length > 0;
    const hasSettings = setting.data.find(
      (item: User) =>
        item.establishment_id === session?.user?.user?.establishment_id,
    );
    const isCashierClosed = cashier?.data?.cashRegisters?.length > 0;

    if (!hasEmployees) {
      missing.push({
        name: "Funcionários",
        message: "Cadastre pelo menos um funcionário.",
        redirect: "/register/employees",
      });
    }

    if (!hasClients) {
      missing.push({
        name: "Clientes",
        message: "Cadastre pelo menos um cliente.",
        redirect: "/register/clients",
      });
    }

    if (!hasServices) {
      missing.push({
        name: "Serviços",
        message: "Cadastre pelo menos um serviço.",
        redirect: "/register/services",
      });
    }

    if (!isCashierClosed) {
      missing.push({
        name: "Caixa",
        message: "Abra o caixa para realizar agendamentos.",
        redirect: "/financial/cashier",
      });
    }

    if (!hasSettings) {
      missing.push({
        name: "Configurações",
        message: "Configure seu estabelecimento.",
        redirect: "settings/general-settings",
      });
    }

    return missing;
  }, [clients, services, employees, setting, cashier, session]);

  const filteredEmployees = useMemo(() => {
    // Mostrar todos os funcionários visíveis e não deletados, independentemente de quem fez login
    return (
      employees?.data?.filter((employee: any) => {
        const isVisible =
          employee.show_in_schedule_and_site === 1 ||
          employee.show_in_schedule_and_site === true ||
          employee.show_in_schedule_and_site === "1";
        const isNotDeleted = employee.deleted_at === null;
        return isNotDeleted && isVisible;
      }) || []
    );
  }, [employees]);

  const filteredPaymentMethods =
    paymentMethods?.data?.filter((method: any) => !method.deleted_at) || [];

  const filteredServices =
    services?.data?.filter((service: any) => service.deleted_at === null) || [];

  const filteredServicesByClient =
    servicesByClient?.data?.filter(
      (service: any) => service.deleted_at === null,
    ) || [];

  const filteredCategories =
    categories?.data?.filter((category: any) => category.deleted_at === null) ||
    [];

  const currentSetting = setting.data.find(
    (item: User) =>
      item.establishment_id === session?.user?.user?.establishment_id,
  );

  const filteredSchedules = useMemo(() => {
    // Remover lógica que restringe à agenda do funcionário logado;
    // sempre retorna todos os agendamentos disponíveis
    return schedules;
  }, [schedules]);

  return {
    register,
    handleSubmit,
    onSubmit,
    reset,
    errors,
    isSubmitting,
    schedules: filteredSchedules,
    clients,
    products,
    employees: filteredEmployees,
    paymentMethods: filteredPaymentMethods,
    services: filteredServices,
    servicesByClient: filteredServicesByClient,
    categories: filteredCategories,
    setting: currentSetting ?? null,
    establishment: session?.user?.user?.establishment,
    cashier,
    loading,
    loadingConfirmation,
    setLoadingConfirmation,
    loadingModalData,
    loadModalData,
    loadPaymentMethodsOnDemand,
    error,
    setScheduleId,
    setServiceID,
    serviceID,
    setProductIdRelation,
    productId,
    scheduleId,
    handleDelete,
    setFormData,
    setValue,
    params,
    setParams,
    handlePage,
    handleDeleteService,
    handleDeleteProduct,
    storeNewService,
    storeProduct,
    updateScheduleProductQuantity,
    markAs,
    reopen,
    getMissingRequirements,
    handleFinalize,
    payments,
    setPayments,
    dispatch,
    isFinalize,
    totalValueCalc,
    setTotalValueCalc,
    watch,
    control,
    handleBlockedTime,
    setSubscriptionItem,
    setSubscriptionUsageItem,
    handleDispatchSchedule,
    clearErrors,
    getServiceByClient,
    updateScheduleServiceQuantity,
  };
};

export default useSchedule;
